import type { FC, ReactNode } from 'react'
import type { LoginRole, ModuleId } from '@/types'

import { BrandLockup } from '@/components/generals/logo'
import { ArrowUpRight, Globe, Heart, MapPin, MessageSquareText } from 'lucide-react'

interface FooterProps {
    onSelectModule: (id: ModuleId) => void
    onOpenLogin: (role: LoginRole, signUp?: boolean) => void
}

const linkClass = 'text-sm text-slate-400 transition-colors hover:text-white'

export const Footer: FC<FooterProps> = ({ onSelectModule, onOpenLogin }): ReactNode => {
    return (
        <footer className='border-t border-white/10 bg-midnight px-6 pb-10 pt-14 text-slate-400'>
            <div className='mx-auto grid max-w-7xl gap-12 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)]'>
                <div>
                    <BrandLockup size={46} subtitle='Gurukul Engineers Hub' />

                    <p className='mt-5 max-w-sm text-sm leading-relaxed'>
                        Faculty of Engineering & Technology, Gurukula Kangri (Deemed to be University), Haridwar.
                    </p>

                    <p className='mt-3 max-w-sm text-sm leading-relaxed text-slate-500'>
                        One platform for the full B.Tech journey: weekly challenges, the Study Vault, campus clubs and T&P placements.
                    </p>
                </div>

                <div>
                    <p className='text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500'>Quick links</p>

                    <ul className='mt-4 space-y-3'>
                        <li>
                            <button type='button' onClick={() => onOpenLogin('st')} className={linkClass}>Sign In</button>
                        </li>

                        <li>
                            <a href='#pricing' className={linkClass}>Register Student Account</a>
                        </li>

                        <li>
                            <button type='button' onClick={() => onSelectModule('journey')} className={linkClass}>32-Month Journey</button>
                        </li>

                        <li>
                            <button type='button' onClick={() => onSelectModule('study-vault')} className={linkClass}>Study Vault</button>
                        </li>

                        <li>
                            <button type='button' onClick={() => onSelectModule('placements')} className={linkClass}>Placements</button>
                        </li>
                    </ul>

                    <span className='mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-2 text-xs font-semibold text-slate-300'>
                        <Heart className='size-3.5 text-rose-400' />
                        Founded & Engineered for GKV FET Haridwar
                    </span>
                </div>

                <div>
                    <p className='text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500'>Contact</p>

                    <ul className='mt-4 space-y-3.5 text-sm'>
                        <li className='flex items-start gap-3'>
                            <MapPin className='mt-0.5 size-4 shrink-0 text-slate-500' />
                            <span>Faculty of Engineering & Technology, Gurukula Kangri (Deemed to be University), Haridwar, Uttarakhand</span>
                        </li>

                        <li>
                            <a href='#contact' className='group flex items-center gap-3 transition-colors hover:text-white'>
                                <MessageSquareText className='size-4 shrink-0 text-slate-500 group-hover:text-white' />
                                Send an inquiry to the Student Pioneer Team
                            </a>
                        </li>

                        <li>
                            <a href='https://gkv.ac.in' target='_blank' rel='noopener noreferrer' className='group flex items-center gap-3 transition-colors hover:text-white'>
                                <Globe className='size-4 shrink-0 text-slate-500 group-hover:text-white' />
                                gkv.ac.in
                                <ArrowUpRight className='size-3.5' />
                            </a>
                        </li>
                    </ul>

                    <div className='mt-6 flex flex-wrap gap-2'>
                        {[
                            ['Student', 'st'],
                            ['Coordinator', 'fp'],
                            ['T&P Officer', 'tp']
                        ].map(([label, role]) => (
                            <button
                                key={role}
                                type='button'
                                onClick={() => onOpenLogin(role as LoginRole)}
                                className='rounded-lg border border-white/10 px-3 py-1.5 text-xs font-semibold text-slate-300 transition hover:border-white/25 hover:text-white'
                            >
                                {label} sign in
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className='mx-auto mt-12 flex max-w-7xl flex-col gap-3 border-t border-white/10 pt-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between'>
                <p>© 2026 GKV Tech Campus · Gurukul Engineers Hub. Built for the students of GKV FET.</p>
                <p>Payments are verified by the T&P Cell before portal access is granted.</p>
            </div>
        </footer>
    )
}
